import { useMemo } from 'react'
import { ArrowLeft, FileText, Loader2, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { ScrollArea } from '@/components/ui/scroll-area'
import type { Document } from '@/types/api'
import type { ExplorerDoc } from './types'

interface UploadsLibraryViewProps {
  documents: Document[]
  loading: boolean
  error: string | null
  onBack: () => void
  onRefresh: () => void | Promise<void>
  onSelectUpload: (doc: ExplorerDoc) => void
}

function getDocumentTimestamp(document: Document): number {
  const rawDate =
    document.uploadedAt ??
    document.createdAt ??
    document.updatedAt ??
    document.processedAt
  if (!rawDate) return 0
  const parsed = Date.parse(rawDate)
  return Number.isNaN(parsed) ? 0 : parsed
}

function formatRelativeDate(dateStr?: string): string {
  if (!dateStr) return 'Unknown time'
  const timestamp = Date.parse(dateStr)
  if (Number.isNaN(timestamp)) return 'Unknown time'

  const diffMs = Date.now() - timestamp
  if (diffMs < 60_000) return 'Just now'
  if (diffMs < 3_600_000) return `${Math.max(1, Math.floor(diffMs / 60_000))} min ago`
  if (diffMs < 86_400_000) return `${Math.max(1, Math.floor(diffMs / 3_600_000))} hours ago`

  const diffDays = Math.floor(diffMs / 86_400_000)
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays} days ago`
  return new Date(timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function toExplorerDoc(document: Document): ExplorerDoc {
  return {
    id: document.id,
    title: document.name?.trim() || `${document.type.toUpperCase()} document`,
    type: document.type === 'pdf' ? 'pdf' : document.type === 'audio' ? 'audio' : 'text',
    confidence: document.extractedData?.categoryConfidence,
  }
}

export function UploadsLibraryView({
  documents,
  loading,
  error,
  onBack,
  onRefresh,
  onSelectUpload,
}: UploadsLibraryViewProps) {
  const sortedDocuments = useMemo(
    () => [...documents].sort((a, b) => getDocumentTimestamp(b) - getDocumentTimestamp(a)),
    [documents]
  )

  return (
    <div className="flex-1 overflow-hidden bg-background p-4 md:p-10">
      <div className="max-w-6xl mx-auto h-full flex flex-col gap-6">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="gap-1.5 shrink-0"
              onClick={onBack}
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <div className="min-w-0">
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight truncate">All Uploads</h2>
              <p className="text-xs md:text-sm text-muted-foreground">
                {sortedDocuments.length} uploaded document{sortedDocuments.length === 1 ? '' : 's'}
              </p>
            </div>
          </div>

          <Button
            type="button"
            size="sm"
            variant="outline"
            className="gap-1.5"
            onClick={onRefresh}
            disabled={loading}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        <div className="flex-1 min-h-0 rounded-2xl border border-slate-200 dark:border-white/15 bg-white dark:bg-secondary overflow-hidden">
          {loading && sortedDocuments.length === 0 ? (
            <div className="h-full flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading uploads...
            </div>
          ) : error ? (
            <div className="h-full flex items-center justify-center p-6 text-sm text-destructive">
              {error}
            </div>
          ) : sortedDocuments.length === 0 ? (
            <div className="h-full flex items-center justify-center p-6 text-sm text-muted-foreground">
              No uploads found.
            </div>
          ) : (
            <ScrollArea className="h-full">
              <div className="divide-y divide-slate-200/80 dark:divide-white/10">
                {sortedDocuments.map((document) => (
                  <button
                    key={document.id}
                    type="button"
                    className="w-full text-left px-4 py-3 md:px-5 md:py-4 flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-tertiary/50 transition-colors"
                    onClick={() => onSelectUpload(toExplorerDoc(document))}
                  >
                    <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                      <FileText className="h-4 w-4" />
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">
                        {document.name?.trim() || `${document.type.toUpperCase()} document`}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Uploaded{' '}
                        {formatRelativeDate(
                          document.uploadedAt ??
                            document.createdAt ??
                            document.updatedAt ??
                            document.processedAt
                        )}
                      </p>
                    </div>

                    <div className="text-[11px] uppercase tracking-wide rounded-full px-2.5 py-1 border border-border/60 text-muted-foreground">
                      {document.status ?? 'pending'}
                    </div>
                  </button>
                ))}
              </div>
            </ScrollArea>
          )}
        </div>
      </div>
    </div>
  )
}
